import styled from '@emotion/styled'
import { Arrow } from 'app/components/Common/Arrow'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { styles } from 'app/theme'
import { easeOutQuint } from 'app/utils/customFunctions'
import useMediaQuery from 'app/utils/useMediaQuery'
import { useKeenSlider } from 'keen-slider/react'
import { uniq } from 'lodash'
import React, { memo, useEffect, useState } from 'react'

export interface Props {
  category?: string
  className?: string
  clickedSlide?: number
  fromModal?: boolean
  images: ImageProps[]
  isOpen?: boolean
}

export const Gallery = memo(function Gallery({
  category,
  className,
  clickedSlide,
  fromModal,
  images,
}: Props) {
  if (!images) {
    return null
  }

  if (images.length < 1) {
    return null
  }

  const [currentSlide, setCurrentSlide] = useState(0)
  const [currentCategory, setCurrentCategory] = useState<any>(category)
  const [opacities, setOpacities] = useState<number[]>([])

  const isMobile = useMediaQuery('(max-width: 1199px)')

  const [sliderRef, galleryRef] = useKeenSlider({
    defaultAnimation: {
      duration: isMobile ? 600 : 1200,
      easing: easeOutQuint,
    },
    loop: images.length > 1 ? true : false,
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel)
      if (!fromModal) {
        setCurrentCategory(
          // @ts-ignore
          images[s.track.details.rel].category,
        )
      }
    },
    detailsChanged(s) {
      const newOpacities = s.track.details.slides.map((slide) => slide.portion)
      setOpacities(newOpacities)
    },
  })

  useEffect(() => {
    if (clickedSlide !== undefined) {
      galleryRef.current?.moveToIdx(clickedSlide, false, { duration: 0 })
    }

    document.addEventListener('keydown', function (e) {
      switch (e.key) {
        case 'ArrowLeft':
          galleryRef.current?.prev()
          break
        case 'ArrowRight':
          galleryRef.current?.next()
          break
      }
    })
  }, [category, currentSlide, clickedSlide, galleryRef, sliderRef])

  return (
    <Container className={className} tabIndex={-1}>
      <Info>{!fromModal ? <Category>{currentCategory}</Category> : null}</Info>

      <Wrapper>
        <Slider
          className={images.length > 1 ? 'grab-cursor' : undefined}
          ref={sliderRef}
        >
          {uniq(images).map((item, index) => (
            <Slide
              className="keen-slider__slide"
              key={index}
              style={{ opacity: opacities[index] }}
            >
              <Image {...item} />
            </Slide>
          ))}
        </Slider>
      </Wrapper>

      {images.length > 1 ? (
        <Arrows>
          <Arrow
            onClick={(e) => e.stopPropagation() || galleryRef.current?.prev()}
          />

          <Arrow
            direction="R"
            onClick={(e) => e.stopPropagation() || galleryRef.current?.next()}
          />
        </Arrows>
      ) : null}

      {!fromModal ? (
        <Counter>{`${currentSlide + 1}/${images.length}`}</Counter>
      ) : null}
    </Container>
  )
})

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralLight2};
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  user-select: none;
`

const Info = styled.div`
  margin-top: 3.75rem;
  text-align: center;
`

const Category = styled.div`
  ${styles.description}

  font-size: 0.875rem;
  line-height: 1.25rem;
`

const Wrapper = styled.div`
  width: calc(100% - 25.556vw);
  height: calc(100% - 11.625rem);
  position: absolute;
  top: 5.8125rem;
  left: 12.778vw;

  @media (max-width: 1199px) {
    width: 100%;
    left: 0;
  }
`

const Slider = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  outline: none;
  overflow: hidden;
`

const Slide = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralLight2};
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  transform: none !important;
  user-select: none;
  transition: opacity 0.2s;

  img {
    display: block;
    width: auto;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    margin: auto;
  }

  @media (max-width: 1199px) {
    display: flex;
    align-items: center;

    img {
      ${styles.img}
    }
  }
`

const Arrows = styled.div`
  > div {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 3;
    &:first-of-type {
      left: 5vw;
    }
    &:last-of-type {
      right: 5vw;
    }
  }

  @media (max-width: 1199px) {
    > div {
      width: 2.625rem;
      height: 2.625rem;
      &:first-of-type {
        left: 1.5rem;
      }
      &:last-of-type {
        right: 1.5rem;
      }

      svg {
        height: 0.75rem;
      }
    }
  }
`

const Counter = styled.div`
  ${styles.description}

  font-size: 0.875rem;
  line-height: 1.25rem;
  position: absolute;
  bottom: 3.75rem;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: 1199px) {
    bottom: 1.5rem;
  }
`
